import { useEffect, useState } from 'react';

/**
 * The lobby header's COMPACT breakpoint — the point where the band drops from
 * the grid header's 96px to a phone's 56px row. In SCSS it is the
 * `compact-header` mixin in _tokens.scss ($bp-mobile, $bp-landscape-h), the one
 * spelling every stylesheet includes; this string is its mirror for the hooks
 * below. Anything in the lobby that re-arranges "when the header changes" keys
 * off one or the other, and __tests__/compactHeader.test.ts compiles the
 * header's stylesheet to prove they are the same query.
 *
 * Today that is the club stats: in the wide header they trail the format
 * pills inside the one filter carousel; under the compact header they take a
 * row of their own beneath the pills (owner, 2026-09-08 — the phone screenshot
 * showed them tacked on the end of the carousel, off-screen until scrolled).
 */
export const COMPACT_LOBBY_HEADER_MEDIA_QUERY = '(max-width: 700px), (max-height: 500px)';

/**
 * The WIDE lobby header (owner, 2026-09-09): above 1024px the promo ribbon
 * leaves the scroll-away band and rides in the header row, left-aligned after
 * the wordmark, so the boxes above the list are no longer a centred ribbon and
 * a centred panel split by the full-width bar. Below it the ribbon is in flow.
 * Mirror of the `wide-header` mixin ($bp-wide) — pinned by the same test.
 */
export const WIDE_LOBBY_HEADER_MEDIA_QUERY = '(min-width: 1025px)';

/** Live match against one media query (same idiom as useIsNarrowTableView). */
function useMediaMatch(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && (window.matchMedia?.(query)?.matches ?? false)
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    if (!mql) return undefined;
    const onChange = () => setMatches(mql.matches);
    // Real MediaQueryLists have one of these; a stubbed matchMedia (tests
    // that only answer `matches`) may have neither — then there is nothing to
    // subscribe to and the initial answer stands.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, [query]);

  return matches;
}

export const useCompactLobbyHeader = (): boolean => useMediaMatch(COMPACT_LOBBY_HEADER_MEDIA_QUERY);
export const useWideLobbyHeader = (): boolean => useMediaMatch(WIDE_LOBBY_HEADER_MEDIA_QUERY);
